/**
 * SPEC §9 – agile werken per werkpakket: sprintcapaciteit en -kleur.
 * Bouwt op de capaciteitsregels van §3.1 (Kalender.vrij).
 */
import { addDagen } from './datum';
import type { Kalender } from './kalender';
import type { ISODate } from './types';

export type ItemStatus = 'BACKLOG' | 'GEPLAND' | 'BEZIG' | 'REVIEW' | 'KLAAR';
export type SprintStatus = 'GEPLAND' | 'LOPEND' | 'AFGESLOTEN';

export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  BACKLOG: 'Backlog',
  GEPLAND: 'Gepland',
  BEZIG: 'Bezig',
  REVIEW: 'Review',
  KLAAR: 'Klaar',
};
/** Kolommen van het bord, in volgorde. */
export const BORD_KOLOMMEN: ItemStatus[] = ['GEPLAND', 'BEZIG', 'REVIEW', 'KLAAR'];

export interface SprintLid {
  engineerId: string;
  inzetPct: number;
}

/** Alle kalenderdagen van start t/m eind. */
export function sprintDagen(start: ISODate, eind: ISODate): ISODate[] {
  const uit: ISODate[] = [];
  for (let d = start; d <= eind && uit.length < 400; d = addDagen(d, 1)) uit.push(d);
  return uit;
}

/** Standaard einddatum: start + sprintlengte in weken − 1 dag. */
export function sprintEind(start: ISODate, weken: number): ISODate {
  return addDagen(start, Math.max(1, weken) * 7 - 1);
}

const rond = (n: number) => Math.round(n * 2) / 2;

/**
 * SPEC §9.2 – capaciteit per teamlid = Σ max(0, vrij) op werkdagen × inzet%;
 * totaal = Σ teamleden × (1 − focusfactor%).
 */
export function sprintCapaciteit(kal: Kalender, leden: SprintLid[], start: ISODate, eind: ISODate, focusfactor: number, terug?: (engineerId: string, d: ISODate) => number) {
  const dagen = sprintDagen(start, eind);
  const perLid = leden.map((l) => {
    const e = kal.engineer(l.engineerId);
    let uren = 0;
    // `terug`: uren van de eigen focusblokken van deze sprint (§9.7) tellen niet als bezet
    if (e) for (const d of dagen) if (kal.isWerkdag(e, d)) uren += Math.max(0, kal.vrij(e, d) + (terug?.(l.engineerId, d) ?? 0));
    return { engineerId: l.engineerId, uren: rond((uren * l.inzetPct) / 100) };
  });
  const bruto = perLid.reduce((s, l) => s + l.uren, 0);
  return { perLid, bruto: rond(bruto), totaal: rond(bruto * (1 - Math.min(90, Math.max(0, focusfactor)) / 100)) };
}

export type SprintKleur = 'groen' | 'geel' | 'rood';
/** SPEC §9.2 – groen ≤ 90%, geel 90–100%, rood > 100% van de capaciteit. */
export function sprintKleur(gepland: number, capaciteit: number): SprintKleur {
  if (gepland > capaciteit) return 'rood';
  if (gepland > capaciteit * 0.9) return 'geel';
  return 'groen';
}

/** SPEC §9.2 – voortgang = uren Klaar / uren totaal. */
export function agileVoortgang(items: { uren: number; status: ItemStatus | string }[]) {
  const totaal = items.reduce((s, i) => s + i.uren, 0);
  const klaar = items.filter((i) => i.status === 'KLAAR').reduce((s, i) => s + i.uren, 0);
  return { totaal, klaar, pct: totaal ? Math.round((klaar / totaal) * 100) : 0 };
}

/** SPEC §9.4 – mag een engineer deze statusovergang zelf doen? (Klaar = product owner) */
export function engineerMagStatus(van: ItemStatus, naar: ItemStatus): boolean {
  if (naar === 'KLAAR' || naar === 'BACKLOG') return false;
  if (van === 'KLAAR') return false;
  return true;
}

// ------------------------------------------------------------------------------------------------
// Fase 2 (SPEC §9.6): budget, velocity, prognose op tempo, burndown en sprintmomenten
// ------------------------------------------------------------------------------------------------

interface ItemUren {
  uren: number;
  status?: ItemStatus | string | null;
  sprintId?: string | null;
  afgerondOp?: string | Date | null;
  restUren?: number | null;
  besteedMin?: number | null;
}

/**
 * SPEC §11.3 – budget van agile items: alleen geschreven uren gaan van het budget af (`ingepland` = besteed).
 * `nogTeDoen` = de rest van de open items (opgave van de engineer, anders schatting − besteed);
 * `klaar` = besteed op items die op Klaar staan.
 */
export function agileUren(items: ItemUren[]) {
  const r = (n: number) => Math.round(n * 100) / 100;
  let besteed = 0;
  let nogTeDoen = 0;
  let klaar = 0;
  for (const i of items) {
    const b = (i.besteedMin ?? 0) / 60;
    besteed += b;
    if (i.status === 'KLAAR') klaar += b;
    else nogTeDoen += i.restUren != null ? Math.max(0, i.restUren) : Math.max(0, (i.uren || 0) - b);
  }
  return { ingepland: r(besteed), besteed: r(besteed), nogTeDoen: r(nogTeDoen), klaar: r(klaar) };
}

export interface SprintInfo {
  id: string;
  nummer: number;
  start: ISODate;
  eind: ISODate;
  status: SprintStatus | string;
}

/** Afgeronde uren in één sprint (items die in die sprint op Klaar zijn gezet). */
export function sprintKlaar(items: ItemUren[], sprintId: string): number {
  return items.filter((i) => i.sprintId === sprintId && i.status === 'KLAAR').reduce((s, i) => s + i.uren, 0);
}

/** SPEC §9.2 – velocity = gemiddelde afgeronde uren van de laatste 3 afgesloten sprints; null zonder afgesloten sprint. */
export function velocity(sprints: SprintInfo[], items: ItemUren[]): { waarde: number | null; sprints: { nummer: number; klaar: number }[] } {
  const af = sprints.filter((s) => s.status === 'AFGESLOTEN').sort((a, b) => b.nummer - a.nummer).slice(0, 3);
  const rij = af.map((s) => ({ nummer: s.nummer, klaar: sprintKlaar(items, s.id) })).reverse();
  if (!rij.length) return { waarde: null, sprints: [] };
  return { waarde: rond(rij.reduce((s, r) => s + r.klaar, 0) / rij.length), sprints: rij };
}

export interface AgilePrognose {
  rest: number;
  tempo: number | null;
  bron: 'velocity' | 'capaciteit' | null;
  sprintsNodig: number;
  eind: ISODate | null;
  teLaat: boolean;
}

/**
 * SPEC §9.2 – prognose = rest ÷ tempo = aantal sprints → einddatum.
 * Tempo is de velocity, of (zonder afgesloten sprint) de capaciteit van de lopende/eerstvolgende sprint.
 * Telling begint bij de lopende sprint, anders de eerstvolgende geplande, anders vandaag.
 */
export function agilePrognose(opts: {
  items: ItemUren[];
  sprints: SprintInfo[];
  sprintWeken: number;
  deadline?: ISODate | null;
  vandaag: ISODate;
  capaciteit?: number | null;
}): AgilePrognose {
  const { items, sprints, sprintWeken, deadline, vandaag, capaciteit } = opts;
  const rest = rond(items.filter((i) => i.status !== 'KLAAR').reduce((s, i) => s + i.uren, 0));
  const v = velocity(sprints, items).waarde;
  const tempo = v && v > 0 ? v : capaciteit && capaciteit > 0 ? capaciteit : null;
  const bron = v && v > 0 ? 'velocity' : tempo ? 'capaciteit' : null;
  if (rest <= 0.01) {
    const laatste = sprints.filter((s) => s.status === 'AFGESLOTEN').sort((a, b) => b.eind.localeCompare(a.eind))[0];
    return { rest: 0, tempo, bron, sprintsNodig: 0, eind: laatste?.eind ?? vandaag, teLaat: false };
  }
  if (!tempo) return { rest, tempo: null, bron: null, sprintsNodig: 0, eind: null, teLaat: false };
  const n = Math.ceil(rest / tempo - 0.001);
  const basis =
    sprints.find((s) => s.status === 'LOPEND') ?? sprints.filter((s) => s.status === 'GEPLAND').sort((a, b) => a.start.localeCompare(b.start))[0];
  const start = basis?.start ?? vandaag;
  const eind = addDagen(start, n * Math.max(1, sprintWeken) * 7 - 1);
  return { rest, tempo, bron, sprintsNodig: n, eind, teLaat: !!deadline && eind > deadline };
}

/** Projectprognose (§3.9) gecombineerd met agile werkpakketten: de laatste datum telt; null als iets onbekend is. */
export function combineerPrognose(klassiek: ISODate | null, klassiekLater: boolean, agile: (ISODate | null)[]): { datum: ISODate | null; later: boolean } {
  if (klassiekLater || agile.some((a) => a === null)) return { datum: null, later: true };
  const alle = [klassiek, ...agile].filter((x): x is ISODate => !!x).sort();
  return { datum: alle.at(-1) ?? null, later: false };
}

const datumVan = (x: string | Date | null | undefined): ISODate | null => (x ? (typeof x === 'string' ? x : x.toISOString()).slice(0, 10) : null);

/**
 * SPEC §9.2 – burndown: per werkdag de resterende uren in de sprint (items van de sprint,
 * min wat op of vóór die dag Klaar is gezet), met een ideale lijn van totaal naar 0.
 * Dagen na vandaag krijgen rest = null.
 */
export function burndown(sprint: SprintInfo, items: ItemUren[], werkdagen: ISODate[], vandaag: ISODate) {
  const mijn = items.filter((i) => i.sprintId === sprint.id);
  const totaal = mijn.reduce((s, i) => s + i.uren, 0);
  const n = werkdagen.length;
  return {
    totaal: rond(totaal),
    punten: werkdagen.map((d, i) => {
      const klaar = mijn.filter((x) => x.status === 'KLAAR' && (datumVan(x.afgerondOp) ?? '9') <= d).reduce((s, x) => s + x.uren, 0);
      return { dag: d, ideaal: rond(n > 1 ? totaal * (1 - i / (n - 1)) : 0), rest: d <= vandaag ? rond(totaal - klaar) : null };
    }),
  };
}

/** Werkdagen (ma–vr, zonder hele feestdagen) tussen start en eind. */
export function sprintWerkdagen(kal: Kalender, start: ISODate, eind: ISODate): ISODate[] {
  return sprintDagen(start, eind).filter((d) => {
    const w = new Date(d + 'T00:00:00Z').getUTCDay();
    return w >= 1 && w <= 5 && !kal.isHeleFeestdag(d);
  });
}

export interface SprintMoment {
  soort: 'PLANNING' | 'STANDUP' | 'REVIEW';
  titel: string;
  start: ISODate;
  eind: ISODate;
  dagen: ISODate[];
  van: string;
  tot: string;
  uren: number;
}

/** Vaste tijden van de sprintmomenten (SPEC §9.6). */
export const SPRINT_TIJDEN = {
  PLANNING: { van: '09:00', tot: '10:00' },
  STANDUP: { van: '09:00', tot: '09:15' },
  REVIEW: { van: '15:00', tot: '16:00' },
} as const;

const urenTussen = (van: string, tot: string) => {
  const m = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  return (m(tot) - m(van)) / 60;
};

/**
 * SPEC §9.6 – sprintmomenten voor de agenda: planning op de eerste werkdag, review en retro
 * op de laatste werkdag en (optioneel) een dagelijkse stand-up op de werkdagen daartussen.
 * Alleen momenten vanaf `vanaf` (vandaag) worden teruggegeven.
 */
export function sprintMomenten(opts: { nummer: number; naam: string; werkdagen: ISODate[]; standup: boolean; vanaf: ISODate }): SprintMoment[] {
  const { nummer, naam, werkdagen, standup, vanaf } = opts;
  if (!werkdagen.length) return [];
  const eerste = werkdagen[0];
  const laatste = werkdagen[werkdagen.length - 1];
  const uit: SprintMoment[] = [];
  const een = (soort: 'PLANNING' | 'REVIEW', titel: string, d: ISODate) => {
    const t = SPRINT_TIJDEN[soort];
    uit.push({ soort, titel, start: d, eind: d, dagen: [d], van: t.van, tot: t.tot, uren: urenTussen(t.van, t.tot) });
  };
  if (eerste >= vanaf) een('PLANNING', `Sprintplanning ${nummer} · ${naam}`, eerste);
  if (standup) {
    const tussen = werkdagen.filter((d) => d !== eerste && d !== laatste && d >= vanaf);
    if (tussen.length) {
      const t = SPRINT_TIJDEN.STANDUP;
      uit.push({ soort: 'STANDUP', titel: `Stand-up sprint ${nummer} · ${naam}`, start: tussen[0], eind: tussen[tussen.length - 1], dagen: tussen, van: t.van, tot: t.tot, uren: urenTussen(t.van, t.tot) });
    }
  }
  if (laatste >= vanaf && laatste !== eerste) een('REVIEW', `Sprintreview en retro ${nummer} · ${naam}`, laatste);
  return uit;
}

/**
 * Prognose van alle agile werkpakketten van een project (SPEC §9.2). Zonder afgesloten sprint
 * is het tempo de capaciteit van de lopende of eerstvolgende sprint.
 */
export function agileProjectPrognoses(opts: {
  kal: Kalender;
  projectId: string;
  werkpakketten: { id: string; projectId: string; naam: string; werkwijze?: string; sprintWeken?: number; focusfactor?: number; deadline?: ISODate | null }[];
  items: (ItemUren & { werkpakketId?: string | null })[];
  sprints: (SprintInfo & { werkpakketId: string; leden: SprintLid[] })[];
  vandaag: ISODate;
}) {
  const { kal, projectId, werkpakketten, items, sprints, vandaag } = opts;
  return werkpakketten
    .filter((w) => w.projectId === projectId && w.werkwijze === 'AGILE')
    .map((w) => {
      const sp = sprints.filter((s) => s.werkpakketId === w.id);
      const it = items.filter((i) => i.werkpakketId === w.id);
      const basis = sp.find((s) => s.status === 'LOPEND') ?? sp.filter((s) => s.status === 'GEPLAND').sort((a, b) => a.start.localeCompare(b.start))[0];
      const cap = basis ? sprintCapaciteit(kal, basis.leden, basis.start, basis.eind, w.focusfactor ?? 20).totaal : null;
      return { werkpakketId: w.id, naam: w.naam, ...agilePrognose({ items: it, sprints: sp, sprintWeken: w.sprintWeken ?? 2, deadline: w.deadline, vandaag, capaciteit: cap }) };
    });
}

// ------------------------------------------------------------------------------------------------
// Fase 3 (SPEC §9.7): punten, focusblokken, sprintwerk in weekrooster en bezetting
// ------------------------------------------------------------------------------------------------

export type Schatting = 'UREN' | 'PUNTEN';
export const EENHEID: Record<Schatting, string> = { UREN: 'u', PUNTEN: 'pt' };

/** Items met de schatting in de gekozen eenheid in het veld `uren`, zodat velocity, prognose en burndown hetzelfde rekenen. */
export function inEenheid<T extends { uren: number; punten?: number | null }>(items: T[], schatting: Schatting | string | undefined): T[] {
  return schatting === 'PUNTEN' ? items.map((i) => ({ ...i, uren: i.punten ?? 0 })) : items;
}

const tijd = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** SPEC §9.7 – focusblok: elke werkdag van de sprint (vanaf `vanaf`) vanaf 13:00 `uren` lang, één terugkerende afspraak per teamlid. */
export function focusBlok(opts: { nummer: number; naam: string; werkdagen: ISODate[]; uren: number; vanaf: ISODate }) {
  const uren = Math.min(4, Math.max(0, opts.uren));
  const dagen = opts.werkdagen.filter((d) => d >= opts.vanaf);
  if (!uren || !dagen.length) return null;
  return { titel: `🎯 Focus sprint ${opts.nummer} · ${opts.naam}`, start: dagen[0], eind: dagen[dagen.length - 1], dagen, van: '13:00', tot: tijd(13 * 60 + Math.round(uren * 60)), uren };
}

/**
 * SPEC §9.7 – sprintwerk per engineer per dag, voor weekrooster en bezetting (alleen ter info; telt niet in vrij).
 * Per sprint (gepland of lopend, werkpakket zonder focusblokken): open items van de eigenaar, plus items zonder
 * eigenaar verdeeld naar inzet%, gelijk verspreid over de resterende werkdagen van dat teamlid.
 */
export function sprintBelasting(opts: {
  kal: Kalender;
  sprints: (SprintInfo & { werkpakketId: string; leden: SprintLid[] })[];
  werkpakketten: { id: string; naam: string; focusUren?: number }[];
  items: { uren: number; status?: string | null; sprintId?: string | null; eigenaarId?: string | null }[];
  vandaag: ISODate;
}): Map<string, Map<ISODate, { uren: number; label: string }>> {
  const { kal, sprints, werkpakketten, items, vandaag } = opts;
  const uit = new Map<string, Map<ISODate, { uren: number; label: string }>>();
  for (const s of sprints) {
    if (s.status === 'AFGESLOTEN' || s.eind < vandaag || !s.leden.length) continue;
    const wp = werkpakketten.find((w) => w.id === s.werkpakketId);
    if (!wp || (wp.focusUren ?? 0) > 0) continue;
    const open = items.filter((i) => i.sprintId === s.id && i.status !== 'KLAAR');
    const vrijItems = open.filter((i) => !i.eigenaarId || !s.leden.some((l) => l.engineerId === i.eigenaarId)).reduce((t, i) => t + i.uren, 0);
    const totInzet = s.leden.reduce((t, l) => t + l.inzetPct, 0) || 1;
    const label = `Sprint ${s.nummer} · ${wp.naam}`;
    for (const l of s.leden) {
      const e = kal.engineer(l.engineerId);
      if (!e) continue;
      const last = open.filter((i) => i.eigenaarId === l.engineerId).reduce((t, i) => t + i.uren, 0) + (vrijItems * l.inzetPct) / totInzet;
      const dagen = sprintDagen(s.start > vandaag ? s.start : vandaag, s.eind).filter((d) => kal.isWerkdag(e, d));
      if (!last || !dagen.length) continue;
      const m = uit.get(l.engineerId) ?? new Map();
      for (const d of dagen) {
        const x = m.get(d);
        m.set(d, { uren: Math.round(((x?.uren ?? 0) + last / dagen.length) * 10) / 10, label: x ? `${x.label}, ${label}` : label });
      }
      uit.set(l.engineerId, m);
    }
  }
  return uit;
}
